import { useEffect, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, Check, Save } from 'lucide-react';
import type { CreatePageInput } from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SuperAdminFallback } from '@/components/auth/super-admin-fallback';
import { useSession } from '@/lib/auth';
import { checkPageSlug, useCreatePage } from '@/lib/pages';

export const Route = createFileRoute('/_authenticated/pages/new')({
  component: NewPagePage,
});

function NewPagePage() {
  const navigate = useNavigate();
  const { user } = useSession();
  const create = useCreatePage();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [slugStatus, setSlugStatus] = useState<{
    state: 'idle' | 'checking' | 'ok' | 'taken' | 'invalid' | 'reserved';
  }>({ state: 'idle' });

  // Auto-derive slug from name when admin hasn't touched it.
  useEffect(() => {
    if (slugTouched) return;
    const auto = name
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^฀-๿a-z0-9]+/gu, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
    setSlug(auto);
  }, [name, slugTouched]);

  // Debounced slug availability check.
  useEffect(() => {
    if (!slug) {
      setSlugStatus({ state: 'idle' });
      return;
    }
    if (slug === 'home') {
      setSlugStatus({ state: 'reserved' });
      return;
    }
    setSlugStatus({ state: 'checking' });
    const t = setTimeout(() => {
      void checkPageSlug(slug).then((r) => {
        if (!r.valid) setSlugStatus({ state: 'invalid' });
        else if (r.available) setSlugStatus({ state: 'ok' });
        else setSlugStatus({ state: 'taken' });
      });
    }, 250);
    return () => clearTimeout(t);
  }, [slug]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) {
      setFormError('กรุณาใส่ชื่อ Page');
      return;
    }
    if (
      slugStatus.state === 'taken' ||
      slugStatus.state === 'invalid' ||
      slugStatus.state === 'reserved'
    ) {
      setFormError('slug ไม่พร้อมใช้');
      return;
    }
    const payload: CreatePageInput = {
      name: name.trim(),
      slug: slug || undefined,
      description: description.trim() || undefined,
    };
    create.mutate(payload, {
      onSuccess: (page) => {
        void navigate({ to: '/pages/$id/edit', params: { id: page.id } });
      },
    });
  };

  if (!user || user.role.slug !== 'super_admin') return <SuperAdminFallback />;

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/pages" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
            <ArrowLeft className="mr-1 h-4 w-4" /> กลับ
          </Link>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
            สร้าง Page
          </h1>
        </div>

        <form
          onSubmit={submit}
          className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="space-y-1.5">
            <Label htmlFor="page-name">ชื่อ Page *</Label>
            <Input
              id="page-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="เช่น หน้าเศรษฐกิจ, Landing Page"
              maxLength={120}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="page-slug">Slug *</Label>
            <div className="relative">
              <Input
                id="page-slug"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugTouched(true);
                }}
                placeholder="economy"
                maxLength={80}
                className="pr-24"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs">
                {slugStatus.state === 'checking' && <span className="text-slate-400">เช็ค…</span>}
                {slugStatus.state === 'ok' && (
                  <span className="flex items-center gap-1 text-emerald-600">
                    <Check className="h-3 w-3" /> ใช้ได้
                  </span>
                )}
                {slugStatus.state === 'taken' && <span className="text-red-500">ใช้แล้ว</span>}
                {slugStatus.state === 'invalid' && (
                  <span className="text-red-500">รูปแบบไม่ถูก</span>
                )}
                {slugStatus.state === 'reserved' && (
                  <span className="text-red-500">"home" สงวน</span>
                )}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              public site ดึงผ่าน <code>/api/public/pages/{slug || '...'}</code>
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="page-desc">คำอธิบาย (optional)</Label>
            <Input
              id="page-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="หมายเหตุสำหรับทีม admin"
              maxLength={500}
            />
          </div>

          {formError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={create.isPending}>
              <Save className="mr-1 h-4 w-4" />
              {create.isPending ? 'กำลังบันทึก…' : 'สร้าง + แก้ Layout ต่อ'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
